import React, { useContext, useState } from 'react';

import classnames from 'classnames';
import { BarChart, MessageSquare, ShieldCheck, ShieldClose, Trash } from 'lucide-react';

import CommentBody from 'common/comment/CommentBody';
import { LocationContext } from 'common/containers/RouterContainer';
import LazyLoadedImage from 'common/LazyLoadedImage';
import DeleteModal from 'common/modals/DeleteModal';
import ModernMarkSpamModal from 'common/modals/ModernMarkSpamModal';
import PostBody from 'common/post/PostBody';
import Spinner from 'common/Spinner';
import Timestamp from 'common/Timestamp';
import truncateString from 'common/truncateString';
import UserLockup from 'common/user/UserLockup';
import queryString from 'common/util/queryString';

import SpamBoxChartIcon from 'img/icons/spambox-chart.svg';

import type { InboxComment } from 'common/api/resources/comments';
import type { InboxPost } from 'common/api/resources/posts';

import 'css/components/subdomain/admin/AdminInbox/_AdminInboxItem.scss';

type ActionResult = Promise<{ error: string | null }>;
enum ActionTypes {
  approve = 'approve',
  delete = 'delete',
  noAction = 'noAction',
  spam = 'spam',
}
type InboxItem = InboxComment | InboxPost;
type Props = {
  item: InboxItem;
  onApprove(item: InboxItem): ActionResult;
  onDelete(item: InboxItem): ActionResult;
  onSpam(item: InboxItem): ActionResult;
};

const MaxTitleChars = 80;

const AdminInboxItem = ({ item, onApprove, onDelete, onSpam }: Props) => {
  // context
  const location = useContext(LocationContext);

  // state
  const [currentAction, setCurrentAction] = useState<ActionTypes>(ActionTypes.noAction);
  const [error, setError] = useState<string>();
  const [modalName, setModalName] = useState<string>();

  // helpers
  const approve = async () => {
    if (currentAction !== ActionTypes.noAction) {
      return;
    }

    setCurrentAction(ActionTypes.approve);
    const { error } = await onApprove(item);

    if (!error) {
      return;
    }

    setCurrentAction(ActionTypes.noAction);
    setError(error);
  };

  const getTargetURL = (user: InboxComment['author'] | InboxPost['author']) => {
    const { pathname, query } = location;
    const newQuery = queryString.stringify({ ...query, userURLName: user?.urlName });
    return `${pathname}${newQuery}`;
  };

  const onConfirmDelete = async () => {
    if (currentAction !== ActionTypes.noAction) {
      return;
    }

    onCloseModal();
    setCurrentAction(ActionTypes.delete);

    const { error } = await onDelete(item);

    if (!error) {
      return;
    }

    setCurrentAction(ActionTypes.noAction);
    setError(error);
  };

  const onConfirmSpam = async () => {
    if (currentAction !== ActionTypes.noAction) {
      return;
    }

    onCloseModal();
    setCurrentAction(ActionTypes.spam);

    const { error } = await onSpam(item);

    if (!error) {
      return;
    }

    setCurrentAction(ActionTypes.noAction);
    setError(error);
  };

  const onDeleteTapped = () => {
    setModalName('delete');
  };

  const onMarkSpamTapped = () => {
    setModalName('spam');
  };

  const onCloseModal = () => {
    setModalName('');
  };

  const renderSpamFlag = (spamScore: number) => {
    let label = 'Low Confidence';
    let confidenceClassName = 'lowConfidence';
    let icon = <LazyLoadedImage src={SpamBoxChartIcon} width={18} alt="Spambox chart icon" />;

    if (spamScore >= 5) {
      label = 'High Confidence';
      confidenceClassName = 'highConfidence';
      icon = <BarChart size={18} />;
    }

    return (
      <div className={classnames('spamFlag', confidenceClassName)}>
        <div className="iconContainer">{icon}</div>
        <div className="label">{label}</div>
      </div>
    );
  };

  // renderers
  const renderHeader = (item: InboxComment | InboxPost) => {
    const isComment = item.itemType === 'comment';
    const post = isComment ? item.post : item;
    const className = classnames('postHead', { commentPost: isComment });
    const title = truncateString(post.title, MaxTitleChars);

    return (
      <div className="itemHeader">
        <div className={className}>
          <div className="headline">
            <p className="title">{title}</p>
            {item.spam && !isComment && renderSpamFlag(item.spamScore)}
          </div>
          <div className="itemInformation">
            <div className="board">{post.board?.name}</div>
            <div className="divider" />
            <div className="author">
              <div>Posted&nbsp;</div>
              <Timestamp timestamp={post.created} textOptions={{ lowerCaseRelative: true }} />
              {post.author && (
                <>
                  <div>&nbsp;by&nbsp;</div>
                  <UserLockup targetURL={getTargetURL(post.author)} user={post.author} />
                </>
              )}
            </div>
          </div>
        </div>
        {isComment && (
          <div className="commentHead">
            <div className="itemInformation">
              <div className="author">
                <div>Commented&nbsp;</div>
                <Timestamp timestamp={item.created} />
                <div>&nbsp;by&nbsp;</div>
                <UserLockup targetURL={getTargetURL(item.author)} user={item.author} />
              </div>
              <div className="commentFlag">
                <div className="iconContainer">
                  <MessageSquare size={15} />
                </div>
                <div className="label">Comment</div>
              </div>
              {item.spam && renderSpamFlag(item.spamScore)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDetails = (item: InboxComment | InboxPost) => {
    const isComment = item.itemType === 'comment';

    if (!isComment && !item.details) {
      return null;
    }

    const body = isComment ? <CommentBody comment={item} /> : <PostBody post={item} />;

    return <div className="itemDetails">{body}</div>;
  };

  const renderFooter = (item: InboxItem) => {
    return (
      <div className="itemFooter">
        <div className="actions">
          <button className="actionButton" onClick={approve}>
            <div className="iconContainer">
              {currentAction === ActionTypes.approve ? <Spinner /> : <ShieldCheck size={18} />}
            </div>
            <div className="label">Mark not spam</div>
          </button>
          <div className="verticalDivider" />
          <button className="actionButton destructiveButton" onClick={onDeleteTapped}>
            <div className="iconContainer">
              {currentAction === ActionTypes.delete ? <Spinner /> : <Trash size={18} />}
            </div>
            <div className="label">Delete {item.itemType}</div>
          </button>
          <button className="actionButton destructiveButton" onClick={onMarkSpamTapped}>
            <div className="iconContainer">
              {currentAction === ActionTypes.spam ? <Spinner /> : <ShieldClose size={18} />}
            </div>
            <div className="label">Ban and delete user</div>
          </button>
        </div>
      </div>
    );
  };

  const renderError = () => {
    if (!error) {
      return null;
    }

    return (
      <div className="errorContainer">
        <div className="error">{error}</div>
      </div>
    );
  };

  const renderModal = () => {
    if (!modalName) {
      return null;
    }

    const isPost = item.itemType === 'post';
    const commonProps = {
      onClose: onCloseModal,
      type: item.itemType,
      ...(isPost && { title: item.title }),
    };

    const modal = {
      spam: <ModernMarkSpamModal {...commonProps} onConfirm={onConfirmSpam} />,
      delete: <DeleteModal {...commonProps} onConfirm={onConfirmDelete} />,
    }[modalName];

    return modal;
  };

  return (
    <div className="adminInboxItem">
      {renderHeader(item)}
      {renderDetails(item)}
      {renderFooter(item)}
      {renderError()}
      {renderModal()}
    </div>
  );
};

export default AdminInboxItem;
